import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ClipboardCopy,
  LogOut,
  Pencil,
  Archive,
  ArchiveRestore,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/useSession";
import { TASK_DEPARTMENTS, type TaskDepartment } from "./tasksConstants";

type AgendaRow = {
  id: string;
  title: string;
  meeting_date: string;
  voting_closes_at: string | null;
  created_at: string;
};

type TopicRow = {
  id: string;
  agenda_id: string;
  department: string;
  title: string;
  details: string | null;
  sort_order: number;
  archived: boolean;
};

type VoteRow = {
  topic_id: string;
  user_id: string;
};

/** Local calendar month as YYYY-MM-DD bounds for Postgres `date` filters. */
function isoMonthRange(d: Date = new Date()) {
  const y = d.getFullYear();
  const m = d.getMonth();
  const pad = (n: number) => String(n).padStart(2, "0");
  const start = `${y}-${pad(m + 1)}-01`;
  const last = new Date(y, m + 1, 0).getDate();
  const end = `${y}-${pad(m + 1)}-${pad(last)}`;
  return { start, end };
}

function isVotingOpen(agenda: AgendaRow): boolean {
  if (!agenda.voting_closes_at) return true;
  return new Date(agenda.voting_closes_at) > new Date();
}

function deptIndex(d: string): number {
  const i = TASK_DEPARTMENTS.indexOf(d as TaskDepartment);
  return i >= 0 ? i : 999;
}

function formatVoteError(raw: string): string {
  if (raw.includes("voting_closed")) {
    return "Voting has closed for this meeting.";
  }
  if (raw.includes("topic_archived")) {
    return "Cannot vote on an archived topic.";
  }
  if (raw.includes("not_editable")) {
    return "This agenda is frozen — voting has closed.";
  }
  return raw || "Something went wrong. Try again.";
}

export function MeetingAgendaPage() {
  const { session } = useSession();
  const uid = session?.user?.id ?? null;

  const [agenda, setAgenda] = useState<AgendaRow | null>(null);
  const [topics, setTopics] = useState<TopicRow[]>([]);
  const [votes, setVotes] = useState<VoteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const [addingTopicDept, setAddingTopicDept] = useState<TaskDepartment>("General");
  const [addingTopicTitle, setAddingTopicTitle] = useState("");
  const [addingTopicDetails, setAddingTopicDetails] = useState("");

  const [editTopicId, setEditTopicId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDetails, setEditDetails] = useState("");
  const [editDept, setEditDept] = useState<TaskDepartment>("General");

  const votingOpen = agenda ? isVotingOpen(agenda) : false;

  const voteCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const v of votes) {
      m.set(v.topic_id, (m.get(v.topic_id) ?? 0) + 1);
    }
    return m;
  }, [votes]);

  const myVotes = useMemo(() => {
    if (!uid) return new Set<string>();
    return new Set(votes.filter((v) => v.user_id === uid).map((v) => v.topic_id));
  }, [votes, uid]);

  const loadTopicsAndVotes = useCallback(async (agendaId: string) => {
    const { data: topicData, error: te } = await supabase
      .from("meeting_agenda_topics")
      .select("id,agenda_id,department,title,details,sort_order,archived")
      .eq("agenda_id", agendaId)
      .order("sort_order")
      .order("title");

    if (te) {
      setErr(te.message);
      setTopics([]);
      setVotes([]);
      return;
    }
    const ts = (topicData as TopicRow[]) ?? [];
    setTopics(ts);

    const ids = ts.map((t) => t.id);
    if (!ids.length) {
      setVotes([]);
      return;
    }

    const { data: voteData, error: ve } = await supabase
      .from("meeting_agenda_topic_votes")
      .select("topic_id,user_id")
      .in("topic_id", ids);

    if (ve) {
      setErr(ve.message);
      setVotes([]);
      return;
    }
    setVotes((voteData as VoteRow[]) ?? []);
  }, []);

  const bootstrap = useCallback(async () => {
    setLoading(true);
    setErr(null);
    setMsg(null);

    const { start, end } = isoMonthRange();
    let pick: AgendaRow | null = null;

    const { data: monthRows, error: me } = await supabase
      .from("meeting_agendas")
      .select("id,title,meeting_date,voting_closes_at,created_at")
      .gte("meeting_date", start)
      .lte("meeting_date", end)
      .order("meeting_date", { ascending: false })
      .limit(1);

    if (me) {
      setErr(me.message);
      setAgenda(null);
      setTopics([]);
      setVotes([]);
      setLoading(false);
      return;
    }

    pick = ((monthRows as AgendaRow[] | null)?.[0] as AgendaRow | undefined) ?? null;

    if (!pick) {
      const { data: latestRows, error: le } = await supabase
        .from("meeting_agendas")
        .select("id,title,meeting_date,voting_closes_at,created_at")
        .order("meeting_date", { ascending: false })
        .limit(1);

      if (le) {
        setErr(le.message);
        setAgenda(null);
        setTopics([]);
        setVotes([]);
        setLoading(false);
        return;
      }
      pick = ((latestRows as AgendaRow[] | null)?.[0] as AgendaRow | undefined) ?? null;
    }

    setAgenda(pick);
    if (pick) await loadTopicsAndVotes(pick.id);
    else {
      setTopics([]);
      setVotes([]);
    }
    setLoading(false);
  }, [loadTopicsAndVotes]);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  const activeTopicsByDept = useMemo(() => {
    const grouped = new Map<string, TopicRow[]>();
    for (const d of TASK_DEPARTMENTS) grouped.set(d, []);
    const active = topics.filter((t) => !t.archived);
    for (const t of active) {
      const list = grouped.get(t.department) ?? grouped.get("General")!;
      list.push(t);
    }
    for (const [_k, list] of grouped) {
      list.sort(
        (a, b) => a.sort_order - b.sort_order || a.title.localeCompare(b.title)
      );
    }
    return grouped;
  }, [topics]);

  const archivedTopics = useMemo(() => topics.filter((t) => t.archived), [topics]);

  async function toggleVote(topicId: string, nextChecked: boolean) {
    if (!uid || !agenda) return;
    setMsg(null);
    setErr(null);
    if (nextChecked) {
      const { error: ie } = await supabase.from("meeting_agenda_topic_votes").insert({
        topic_id: topicId,
        user_id: uid,
      });
      if (ie) {
        setErr(formatVoteError(ie.message));
        return;
      }
    } else {
      const { error: de } = await supabase
        .from("meeting_agenda_topic_votes")
        .delete()
        .eq("topic_id", topicId)
        .eq("user_id", uid);
      if (de) {
        setErr(formatVoteError(de.message));
        return;
      }
    }
    await loadTopicsAndVotes(agenda.id);
    setMsg(nextChecked ? "Vote saved." : "Vote removed.");
  }

  async function addTopic(e: React.FormEvent) {
    e.preventDefault();
    if (!agenda || !addingTopicTitle.trim()) return;
    const maxSort =
      topics.filter((x) => x.agenda_id === agenda.id).reduce(
        (m, t) => Math.max(m, t.sort_order),
        0
      ) ?? 0;

    const { error } = await supabase.from("meeting_agenda_topics").insert({
      agenda_id: agenda.id,
      department: addingTopicDept,
      title: addingTopicTitle.trim(),
      details: addingTopicDetails.trim() || null,
      sort_order: maxSort + 1,
      archived: false,
    });

    if (error) {
      setErr(formatVoteError(error.message));
      return;
    }
    setAddingTopicTitle("");
    setAddingTopicDetails("");
    await loadTopicsAndVotes(agenda.id);
    setMsg("Topic added.");
  }

  function openEdit(topic: TopicRow) {
    setEditTopicId(topic.id);
    setEditTitle(topic.title);
    setEditDetails(topic.details ?? "");
    setEditDept((TASK_DEPARTMENTS.includes(topic.department as TaskDepartment)
      ? topic.department
      : "General") as TaskDepartment);
  }

  async function saveEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!editTopicId || !agenda || !editTitle.trim()) return;
    const { error } = await supabase
      .from("meeting_agenda_topics")
      .update({
        title: editTitle.trim(),
        details: editDetails.trim() || null,
        department: editDept,
      })
      .eq("id", editTopicId);

    if (error) {
      setErr(formatVoteError(error.message));
      return;
    }
    setEditTopicId(null);
    await loadTopicsAndVotes(agenda.id);
    setMsg("Topic updated.");
  }

  async function setArchived(topic: TopicRow, archived: boolean) {
    if (!agenda) return;
    const { error } = await supabase
      .from("meeting_agenda_topics")
      .update({ archived })
      .eq("id", topic.id);
    if (error) {
      setErr(formatVoteError(error.message));
      return;
    }
    await loadTopicsAndVotes(agenda.id);
    setMsg(archived ? "Topic archived." : "Topic restored.");
  }

  function copyPriorityList() {
    if (!agenda) return;
    const lines: string[] = [];
    lines.push(agenda.title);
    lines.push(`Meeting date: ${agenda.meeting_date}`);
    lines.push("");

    const active = topics.filter((t) => !t.archived);
    const sorted = [...active].sort((a, b) => {
      const ca = voteCounts.get(a.id) ?? 0;
      const cb = voteCounts.get(b.id) ?? 0;
      if (cb !== ca) return cb - ca;
      const da = deptIndex(a.department);
      const db = deptIndex(b.department);
      if (da !== db) return da - db;
      return a.title.localeCompare(b.title);
    });

    let rank = 1;
    for (const t of sorted) {
      const c = voteCounts.get(t.id) ?? 0;
      lines.push(`${rank}. [${t.department}] ${t.title} — ${c} vote${c === 1 ? "" : "s"}`);
      if (t.details?.trim()) lines.push(`   ${t.details.trim()}`);
      rank += 1;
    }

    const text = lines.join("\n");
    void navigator.clipboard.writeText(text);
    setMsg("Priority list copied to clipboard.");
  }

  const { start: monthStart, end: monthEnd } = isoMonthRange();

  return (
    <div className="min-h-screen bg-brand-gray text-brand-blue">
      <div className="mx-auto max-w-6xl px-4 py-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link
              to="/exco"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white text-brand-blue shadow-sm ring-1 ring-black/10 hover:bg-brand-gray/20"
              title="Back to ExCo"
              aria-label="Back to ExCo"
            >
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <p className="font-display text-2xl font-bold">Meeting topics</p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {session?.user?.email ? (
              <div className="hidden max-w-[16rem] truncate rounded-full bg-white px-3 py-2 text-sm font-semibold text-brand-blue/80 shadow-sm ring-1 ring-black/10 sm:block">
                {session.user.email}
              </div>
            ) : null}
            <button
              type="button"
              title="Copy priority list"
              onClick={() => copyPriorityList()}
              disabled={!agenda}
              className="inline-flex items-center gap-2 rounded-full bg-brand-blue px-4 py-2 text-xs font-bold uppercase tracking-wide text-brand-gray shadow-sm hover:opacity-95 disabled:opacity-40"
            >
              <ClipboardCopy className="h-4 w-4" />
              Copy list
            </button>
            <button
              type="button"
              title="Log out"
              aria-label="Log out"
              onClick={() => void supabase.auth.signOut()}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white text-brand-blue shadow-sm ring-1 ring-black/10 hover:bg-brand-gray/20"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>

        {loading ? (
          <p className="mt-12 text-center text-brand-blue/70">Loading…</p>
        ) : null}

        {err ? (
          <div className="mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {err}
          </div>
        ) : null}
        {msg ? (
          <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
            {msg}
          </div>
        ) : null}

        {!loading && !agenda ? (
          <p className="mt-10 max-w-xl text-brand-blue/80">
            No meeting agenda found for <span className="font-semibold">{monthStart}</span>–
            <span className="font-semibold">{monthEnd}</span> (or any earlier record). Add one in Supabase
            or run the seed SQL, then refresh this page.
          </p>
        ) : null}

        {agenda ? (
          <div className="mt-8 rounded-2xl border border-black/10 bg-white p-4 shadow-sm">
            <h2 className="font-display text-xl font-bold text-brand-blue">{agenda.title}</h2>
            <p className="mt-1 text-sm text-brand-blue/75">Meeting date: {agenda.meeting_date}</p>
            <div className="mt-4 space-y-1 text-sm text-brand-blue/80">
              <p>
                Tick every topic you want discussed — <span className="font-semibold">there is no vote limit</span>.
              </p>
              {agenda.voting_closes_at ? (
                <p>
                  Voting closes:{" "}
                  <span className="font-medium text-brand-blue">
                    {new Date(agenda.voting_closes_at).toLocaleString()}
                  </span>
                </p>
              ) : (
                <p>No voting deadline set — voting stays open until a deadline is set in the database.</p>
              )}
              {!votingOpen ? (
                <p className="font-semibold text-amber-700">This meeting is frozen — votes and edits are locked.</p>
              ) : null}
            </div>
          </div>
        ) : null}

        {agenda && votingOpen ? (
          <form
            className="mt-6 rounded-2xl border border-black/10 bg-white p-4 shadow-sm"
            onSubmit={addTopic}
          >
            <p className="font-display text-lg font-bold">Add topic</p>
            <div className="mt-4 grid gap-3 md:grid-cols-12">
              <div className="md:col-span-3">
                <label className="text-xs font-bold uppercase tracking-wider text-brand-blue/55">Department</label>
                <select
                  className="mt-1 w-full rounded-xl border border-black/15 px-3 py-2 text-sm"
                  value={addingTopicDept}
                  onChange={(e) => setAddingTopicDept(e.target.value as TaskDepartment)}
                >
                  {TASK_DEPARTMENTS.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-6">
                <label className="text-xs font-bold uppercase tracking-wider text-brand-blue/55">Title</label>
                <input
                  className="mt-1 w-full rounded-xl border border-black/15 px-3 py-2 text-sm"
                  value={addingTopicTitle}
                  onChange={(e) => setAddingTopicTitle(e.target.value)}
                  placeholder="Brief topic heading"
                  required
                />
              </div>
              <div className="md:col-span-3 flex items-end">
                <button
                  type="submit"
                  className="w-full rounded-xl bg-brand-blue py-2 text-xs font-bold uppercase tracking-wide text-brand-gray hover:opacity-95"
                >
                  Add topic
                </button>
              </div>
              <div className="md:col-span-12">
                <label className="text-xs font-bold uppercase tracking-wider text-brand-blue/55">
                  Details (optional)
                </label>
                <textarea
                  className="mt-1 w-full rounded-xl border border-black/15 px-3 py-2 text-sm"
                  rows={2}
                  value={addingTopicDetails}
                  onChange={(e) => setAddingTopicDetails(e.target.value)}
                  placeholder="Context for the secretary or discussion lead"
                />
              </div>
            </div>
          </form>
        ) : null}

        <div className="mt-8 space-y-8">
          {TASK_DEPARTMENTS.map((dept) => {
            const list = activeTopicsByDept.get(dept) ?? [];
            if (!list.length) return null;
            return (
              <section key={dept}>
                <h2 className="font-display text-xl font-bold text-brand-blue">{dept}</h2>
                <ul className="mt-3 space-y-3">
                  {list.map((t) => (
                    <li
                      key={t.id}
                      className="flex flex-wrap items-start gap-3 rounded-2xl border border-black/10 bg-white px-4 py-3 shadow-sm"
                    >
                      <div className="pt-1">
                        <input
                          type="checkbox"
                          checked={myVotes.has(t.id)}
                          disabled={!votingOpen || !uid}
                          onChange={(e) => void toggleVote(t.id, e.target.checked)}
                          className="h-5 w-5 accent-brand-maroon"
                          aria-label={`Vote for ${t.title}`}
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-brand-blue">{t.title}</p>
                        {t.details ? (
                          <p className="mt-1 whitespace-pre-wrap text-sm text-brand-blue/75">{t.details}</p>
                        ) : null}
                        <div className="mt-2 flex flex-wrap gap-3 text-xs text-brand-blue/60">
                          <span>
                            {voteCounts.get(t.id) ?? 0} vote
                            {(voteCounts.get(t.id) ?? 0) === 1 ? "" : "s"}
                          </span>
                          {votingOpen ? (
                            <>
                              <button
                                type="button"
                                onClick={() => openEdit(t)}
                                className="inline-flex items-center gap-1 font-semibold text-brand-maroon underline-offset-2 hover:underline"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => void setArchived(t, true)}
                                className="inline-flex items-center gap-1 font-semibold text-brand-maroon underline-offset-2 hover:underline"
                              >
                                <Archive className="h-3.5 w-3.5" />
                                Archive
                              </button>
                            </>
                          ) : null}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>

        {archivedTopics.length > 0 ? (
          <section className="mt-10 rounded-2xl border border-black/10 border-dashed bg-white/70 p-4">
            <h2 className="font-display text-lg font-bold text-brand-blue/80">Archived topics</h2>
            <p className="mt-1 text-sm text-brand-blue/60">
              Hidden from voting; previous votes still count toward totals.
            </p>
            <ul className="mt-4 space-y-2">
              {archivedTopics.map((t) => (
                <li
                  key={t.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-brand-gray/40 px-3 py-2 text-sm"
                >
                  <span className="text-brand-blue/80">
                    [{t.department}] {t.title}
                  </span>
                  <span className="text-brand-blue/50">
                    {voteCounts.get(t.id) ?? 0} votes
                  </span>
                  {votingOpen ? (
                    <button
                      type="button"
                      onClick={() => void setArchived(t, false)}
                      className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-brand-maroon hover:underline"
                    >
                      <ArchiveRestore className="h-3.5 w-3.5" />
                      Restore
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {editTopicId ? (
          <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/40 p-4 md:items-center">
            <button
              type="button"
              aria-label="Close"
              className="absolute inset-0 cursor-default"
              onClick={() => setEditTopicId(null)}
            />
            <div
              role="dialog"
              aria-modal="true"
              className="relative z-[101] w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl ring-1 ring-black/10"
            >
              <p className="font-display text-xl font-bold">Edit topic</p>
              <form className="mt-4 space-y-3" onSubmit={(e) => void saveEdit(e)}>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-brand-blue/55">
                    Department
                  </label>
                  <select
                    className="mt-1 w-full rounded-xl border border-black/15 px-3 py-2 text-sm"
                    value={editDept}
                    onChange={(e) => setEditDept(e.target.value as TaskDepartment)}
                  >
                    {TASK_DEPARTMENTS.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-brand-blue/55">
                    Title
                  </label>
                  <input
                    className="mt-1 w-full rounded-xl border border-black/15 px-3 py-2 text-sm"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-brand-blue/55">
                    Details
                  </label>
                  <textarea
                    className="mt-1 w-full rounded-xl border border-black/15 px-3 py-2 text-sm"
                    rows={3}
                    value={editDetails}
                    onChange={(e) => setEditDetails(e.target.value)}
                  />
                </div>
                <div className="flex flex-wrap justify-end gap-2 pt-2">
                  <button
                    type="button"
                    className="rounded-full px-5 py-2 text-sm font-semibold text-brand-blue/80 hover:bg-brand-gray/50"
                    onClick={() => setEditTopicId(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="rounded-full bg-brand-blue px-5 py-2 text-sm font-bold text-brand-gray hover:opacity-95"
                  >
                    Save
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
